import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import {
  FiMenu,
  FiX,
  FiChevronDown,
  FiBookOpen,
  FiVideo,
  FiMic,
  FiBriefcase,
  FiTrendingUp,
  FiCalendar,
  FiHeart,
  FiHome,
  FiShoppingBag,
  FiActivity,
  FiZap,
  FiSun,
  FiMoon,
} from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext';
import './Navigation.css';

const RESOURCES = [
  { to: '/resources/blog', icon: FiBookOpen, title: 'Blog', desc: 'Playbooks, teardowns, case studies.' },
  { to: '/resources/seo', icon: FiTrendingUp, title: 'SEO Guides', desc: 'Step-by-step technical & on-page SEO.' },
  { to: '/resources/videos', icon: FiVideo, title: 'Videos', desc: 'Audit + design walkthroughs.' },
  { to: '/resources/webinars', icon: FiMic, title: 'Webinars', desc: 'Live sessions with Q&A.' },
  { to: '/resources/case-studies', icon: FiBriefcase, title: 'Case Studies', desc: 'Real projects, real numbers.' },
  { to: '/resources/blog', icon: FiZap, title: 'Optimisation Tips', desc: 'Quick fixes you can apply today.' },
];

const INDUSTRIES = [
  { to: '/industries/healthcare', icon: FiHeart, title: 'Healthcare', desc: 'Clinics, hospitals, telehealth.' },
  { to: '/industries/real-estate', icon: FiHome, title: 'Real Estate', desc: 'Builders, brokers, listings.' },
  { to: '/industries/ecommerce', icon: FiShoppingBag, title: 'Ecommerce', desc: 'D2C, marketplaces, B2B.' },
  { to: '/industries/natural-nutrition', icon: FiActivity, title: 'Natural & Nutrition', desc: 'Wellness, organic, supplements.' },
  { to: '/industries/education', icon: FiBookOpen, title: 'Education', desc: 'Schools, coaching, EdTech.' },
];

const ThemeToggle = ({ variant }) => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      className={`theme-toggle theme-toggle--${variant}`}
      onClick={toggleTheme}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? <FiSun /> : <FiMoon />}
      {variant === 'mobile' && <span>{isDark ? 'Light mode' : 'Dark mode'}</span>}
    </button>
  );
};

const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState(null); // 'industries' | 'resources' | null
  const closeTimer = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setActive(null);
  }, [pathname]);

  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const onEnter = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActive(key);
  };

  const onLeave = () => {
    closeTimer.current = setTimeout(() => setActive(null), 140);
  };

  const closeMenu = () => setOpen(false);

  const renderMenuItems = (variant) => (
    <>
      <NavLink end to="/" onClick={closeMenu} className={({ isActive }) => 'nav-link' + (isActive ? ' nav-link--active' : '')}>
        Home
      </NavLink>
      <NavLink to="/services" onClick={closeMenu} className={({ isActive }) => 'nav-link' + (isActive ? ' nav-link--active' : '')}>
        Services
      </NavLink>
      <NavLink to="/process" onClick={closeMenu} className={({ isActive }) => 'nav-link' + (isActive ? ' nav-link--active' : '')}>
        Process
      </NavLink>

      <div
        className={`nav-dropdown ${active === `industries-${variant}` ? 'open' : ''}`}
        onMouseEnter={variant === 'desktop' ? () => onEnter(`industries-${variant}`) : undefined}
        onMouseLeave={variant === 'desktop' ? onLeave : undefined}
      >
        <button
          className="nav-link nav-link--button"
          onClick={() => setActive(active === `industries-${variant}` ? null : `industries-${variant}`)}
          aria-expanded={active === `industries-${variant}`}
        >
          Industries <FiChevronDown />
        </button>
        <div className="mega-panel mega-panel--single">
          <div className="mega-grid">
            {INDUSTRIES.map((i) => (
              <Link key={i.to} to={i.to} className="mega-item" onClick={() => { setActive(null); closeMenu(); }}>
                <span className="mega-item-icon"><i.icon /></span>
                <div>
                  <strong>{i.title}</strong>
                  <span>{i.desc}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div
        className={`nav-dropdown ${active === `resources-${variant}` ? 'open' : ''}`}
        onMouseEnter={variant === 'desktop' ? () => onEnter(`resources-${variant}`) : undefined}
        onMouseLeave={variant === 'desktop' ? onLeave : undefined}
      >
        <button
          className="nav-link nav-link--button"
          onClick={() => setActive(active === `resources-${variant}` ? null : `resources-${variant}`)}
          aria-expanded={active === `resources-${variant}`}
        >
          Resources <FiChevronDown />
        </button>
        <div className="mega-panel">
          <div className="mega-grid mega-grid--two">
            {RESOURCES.map((r) => (
              <Link key={r.title} to={r.to} className="mega-item" onClick={() => { setActive(null); closeMenu(); }}>
                <span className="mega-item-icon"><r.icon /></span>
                <div>
                  <strong>{r.title}</strong>
                  <span>{r.desc}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <NavLink to="/about" onClick={closeMenu} className={({ isActive }) => 'nav-link' + (isActive ? ' nav-link--active' : '')}>
        About
      </NavLink>
      <NavLink to="/contact" onClick={closeMenu} className={({ isActive }) => 'nav-link' + (isActive ? ' nav-link--active' : '')}>
        Contact
      </NavLink>

      <ThemeToggle variant={variant} />

      <Link to="/schedule" className="nav-cta" onClick={closeMenu}>
        <FiCalendar /> Schedule meeting
      </Link>
    </>
  );

  return (
    <>
      <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}>
        <div className="navbar-container">
          <Link to="/" className="navbar-logo" onClick={closeMenu}>
            <span className="logo-mark logo-mark--image">
              <img src="/img/company-logo.png" alt="THINKINGWORLD" />
            </span>
            <span className="logo-text">
              THINKING<span className="logo-faint">WORLD</span>
            </span>
          </Link>

          <div className="nav-menu nav-menu--desktop">
            {renderMenuItems('desktop')}
          </div>

          <button className="hamburger" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
            {open ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </nav>

      {open && <div className="nav-backdrop" onClick={closeMenu} aria-hidden="true" />}
      <aside className={`nav-drawer ${open ? 'nav-drawer--open' : ''}`} aria-hidden={!open}>
        <div className="nav-drawer-inner">
          {renderMenuItems('mobile')}
        </div>
      </aside>
    </>
  );
};

export default Navigation;
